import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import CTASection from "../CTASection";
import HowItWorksSection from "../HowItWorksSection";
import JobPostSection from "../JobPostSection";
import SalesData from "../Sales/SalesData";
import TrustedBySection from "../TrustedBySection";
import UniqueSelfSync from "../UniqueSelfsync";
import HeroSection from "./HeroSection";
import HiringNewCandidate from "./HiringNewCandidate";
import WhyEPPSection from "./WhyEPPSection";
import WhyItWorks from "./WhyItWorks";
import HowItWorks from "./HowItWorks";
import Testimonials from "../Testimonials/Testimonials";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaRocket,
  FaQuestionCircle,
  FaUser,
  FaBuilding,
  FaArrowRight,
} from "react-icons/fa";
import EmailCapture from "../../common/EmailCapture";

// Simple Explanation Section for First-Time Users
const SimpleExplanation = () => {
  return (
    <section className="py-8 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 max-md:mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <FaQuestionCircle />
            What is SelfSync?
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            It’s Like a Cheat Code for Talent Decisions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get insights into real personality traits so you can hire
            confidently, promote fairly, and build high-performing teams
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* For Individuals */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 max-md:h-10 max-md:w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <FaBuilding className="text-purple-600 text-xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                For Companies
              </h3>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-purple-500 rounded-full  max-md:px-2 text-white text-xs flex items-center justify-center font-bold mt-1">
                  1
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">
                    Send personality tests to candidates
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Instead of just looking at resumes
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-purple-500 max-md:px-2 rounded-full text-white text-xs flex items-center justify-center font-bold mt-1">
                  2
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">
                    See who fits your job
                  </h4>
                  <p className="text-gray-600 text-sm">
                    AI tells you which personalities succeed in your specific
                    role
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-purple-500 max-md:px-2 rounded-full text-white text-xs flex items-center justify-center font-bold mt-1">
                  3
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">
                    Hire people who actually succeed
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Stop hiring the wrong people and reduce employee turnover
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-purple-700">
                <strong>Real Example:</strong> "Instead of hiring salespeople
                based on charisma, we now hire based on resilience and
                persistence - our sales team retention improved by 60%."
              </p>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 max-md:h-10 max-md:w-10 bg-green-100 rounded-lg flex items-center justify-center">
                <FaUser className="text-green-600 text-xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                For You (Job Seekers)
              </h3>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-500 rounded-full max-md:px-2 text-white text-xs flex items-center justify-center font-bold mt-1">
                  1
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">
                    Take a 30 minute personality test
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Answer questions about how you work and think
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-500 rounded-full max-md:px-2 text-white text-xs flex items-center justify-center font-bold mt-1">
                  2
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">
                    Get your personality report
                  </h4>
                  <p className="text-gray-600 text-sm">
                    See your strengths, work style, and ideal environments
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-500 rounded-full max-md:px-2 text-white text-xs flex items-center justify-center font-bold mt-1">
                  3
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">
                    Find your perfect career match
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Discover jobs and companies where you'll actually be happy
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-700">
                <strong>Real Example:</strong> "I learned I'm naturally
                detail-oriented and work best in structured environments - now I
                focus on roles in accounting and project management instead of
                creative fields."
              </p>
            </div>
          </motion.div>

          {/* For Businesses */}
        </div>

        {/* Bottom Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 max-md:mt-8 text-center"
        >
          <div className="bg-white rounded-2xl p-8 shadow-lg max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              The Bottom Line
            </h3>
            <p className="text-lg text-gray-700 leading-relaxed">
              We use AI to understand personalities, just like a psychologist
              would. This helps job seekers find careers where they'll be happy
              and successful, and helps companies hire people who will actually
              thrive in their roles. It's that simple.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <div className="bg-gray-100 px-2 md:px-6 py-3 rounded-lg">
                <span className="text-sm text-gray-600">
                  ✓ No psychology knowledge needed
                </span>
              </div>
              <div className="bg-gray-100 px-6 py-3 rounded-lg">
                <span className="text-sm text-gray-600">
                  ✓ Takes just 30 minutes
                </span>
              </div>
              <div className="bg-gray-100 px-6 py-3 rounded-lg">
                <span className="text-sm text-gray-600">
                  ✓ Works for any industry
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Floating Action Bar Component
const FloatingActionBar = () => {
  const [isVisible, setIsVisible] = useState(false);
  const loginUrl = "https://portal.selfsync.ai/sign-up";

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-purple-600 to-cyan-500 text-white shadow-2xl overflow-hidden"
        >
          <div className="max-w-7xl mx-auto px-4 py-2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 max-md:gap-1 text-center sm:text-left">
              {/* Text and icon */}
              <div className="flex flex-col sm:flex-row justify-center items-center gap-3 max-md:gap-0 text-center sm:text-left">
                <div className="flex items-center gap-2">
                  <FaRocket className="text-xl text-white" />
                  <p className="font-bold text-lg">Ready to Try It?</p>
                </div>
                <p className="text-sm opacity-90 sm:mt-0 mt-2 max-md:mt-0">
                  Free personality test - see what careers fit you
                </p>
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 max-md:gap-1 sm:gap-4">
                <button
                  onClick={() => window.open(loginUrl, "_blank")}
                  className="bg-white text-purple-600 font-bold px-6 py-3 max-md:py-1.5 rounded-lg hover:bg-gray-50 transition-all shadow-lg  "
                >
                  Take Free Test
                </button>
                <span className="text-base max-md:text-sm opacity-95 text-white">
                  30 minutes only
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

function DashBoard() {
  const [showModalSales, setShowModalSales] = useState(false);
  const [modalSource, setModalSource] = useState("");
  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to top on component mount
  }, []);

  useEffect(() => {
    if (showModalSales) {
      document.body.style.overflow = "hidden"; // 🚫 disable scroll
    } else {
      document.body.style.overflow = "auto"; // ✅ enable scroll again
    }

    return () => {
      document.body.style.overflow = "auto"; // ✅ fallback clean-up
    };
  }, [showModalSales]);

  const handleOpenModal = (source) => {
    setModalSource(source);
    setShowModalSales(true);
  };

  const closeModalSale = () => {
    setShowModalSales(false);
    setModalSource("");
  };

  return (
    <div className="overflow-hidden">
      <Helmet>
        <title>
          Free Personality Test for Career & Hiring Success | SelfSync AI
        </title>
        <meta
          name="description"
          content="Take a free 30-minute personality test to find your perfect career match or hire people who actually succeed. Used by 260+ companies and 50,000+ professionals."
        />
        <meta
          name="keywords"
          content="personality test, career assessment, free personality test, hiring assessment, job matching, career finder"
        />
        <link rel="canonical" href="https://selfsync.ai/" />
      </Helmet>

      <HeroSection handleOpenModal={handleOpenModal} />
      <SimpleExplanation />
      <HowItWorks handleOpenModal={handleOpenModal} />
      {/* <WhyEPPSection handleOpenModal={handleOpenModal} /> */}
      <HiringNewCandidate handleOpenModal={handleOpenModal} />

      <WhyItWorks handleOpenModal={handleOpenModal} />

      <TrustedBySection handleOpenModal={handleOpenModal} />
      <Testimonials handleOpenModal={handleOpenModal} />

      {/* Floating Action Bar */}
      <FloatingActionBar />

      {/* Smart Email Capture */}
      <EmailCapture />

      {showModalSales && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-5 md:px-0">
          <div className="relative w-full max-w-2xl bg-white rounded-xl ">
            <button
              className="absolute top-2 right-3 text-black hover:text-gray-600 text-3xl font-semibold z-10"
              onClick={closeModalSale}
              aria-label="Close"
            >
              &times;
            </button>

            <div className="">
              {modalSource === "waitingList" ? (
                <div>
                  {/* <h2 className="text-xl font-bold mb-4">Join Our Waiting List</h2> */}
                  <SalesData
                    source={modalSource}
                    closeModalSale={closeModalSale}
                  />
                </div>
              ) : (
                <SalesData
                  source={modalSource}
                  closeModalSale={closeModalSale}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashBoard;
